import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const getFavicon = (url) => {
  try { return `https://www.google.com/s2/favicons?sz=64&domain_url=${new URL(url).origin}`; }
  catch { return null; }
};

function PlatformLogo({ platform, size = 48 }) {
  const [imgSrc, setImgSrc] = useState(platform.logo || getFavicon(platform.downloadUrl));
  const [failed, setFailed] = useState(false);

  if (failed || !imgSrc) {
    return (
      <div style={{ width: size, height: size, background: 'var(--gradient-primary)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: size * 0.4, color: '#fff' }}>
        {platform.name[0]}
      </div>
    );
  }
  return (
    <img src={imgSrc} alt={platform.name}
      onError={() => { if (platform.logo) setImgSrc(getFavicon(platform.downloadUrl)); else setFailed(true); }}
      style={{ width: size, height: size, borderRadius: 10, objectFit: 'contain', background: 'var(--bg-secondary)', padding: 4 }}
    />
  );
}

const empty = { name: '', downloadUrl: '', description: '', isHottest: false, isActive: true, label: '', order: 0 };

export default function AdminPlatforms() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/platforms/admin').then(({ data }) => setPlatforms(data.platforms || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setLogoFile(null); setLogoPreview(null); setError(''); setModal(true); };
  const openEdit = (p) => {
    setEditing(p._id);
    setForm({ name: p.name, downloadUrl: p.downloadUrl, description: p.description || '', isHottest: p.isHottest, isActive: p.isActive, label: p.label || '', order: p.order || 0 });
    setLogoFile(null);
    setLogoPreview(p.logo || null);
    setError('');
    setModal(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (logoFile) fd.append('logo', logoFile);
      if (editing) await api.put(`/platforms/${editing}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      else await api.post('/platforms', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this platform?')) return;
    await api.delete(`/platforms/${id}`); load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>PLATFORMS</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Upload logos for each platform to display on the site</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Platform</button>
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
        <div className="grid-3">
          {platforms.map(p => (
            <div key={p._id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <PlatformLogo platform={p} size={52} />
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, marginBottom: 4 }}>{p.name}</h3>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {p.isHottest && <span className="badge badge-hot" style={{ fontSize: 10 }}>🔥 HOT</span>}
                      {p.label && <span className={`badge badge-${p.label.toLowerCase()}`} style={{ fontSize: 10 }}>{p.label}</span>}
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: p.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: p.isActive ? '#10b981' : '#ef4444' }}>{p.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Logo status indicator */}
              <div style={{ fontSize: 12, color: p.logo ? '#10b981' : 'var(--text-muted)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                {p.logo ? '✅ Custom logo uploaded' : '🔄 Using auto-fetched favicon'}
              </div>

              {p.description && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>{p.description}</p>}
              <a href={p.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--accent-cyan)', display: 'block', marginBottom: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.downloadUrl}</a>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={() => openEdit(p)}>✏ Edit / Upload Logo</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p._id)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 24 }}>{editing ? 'Edit Platform' : 'Add Platform'}</h2>
            {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px', color: '#ef4444', marginBottom: 16, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleSave}>

              {/* Logo Upload — prominent at top */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, letterSpacing: '0.05em' }}>PLATFORM LOGO</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: 'var(--bg-secondary)', border: '2px dashed var(--border)', borderRadius: 12, padding: 16 }}>
                  {/* Preview */}
                  <div style={{ width: 72, height: 72, borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {logoPreview ? (
                      <img src={logoPreview} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                    ) : (
                      <span style={{ fontSize: 28, color: 'var(--text-muted)' }}>🖼</span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ marginBottom: 6 }}
                      id="logo-upload"
                    />
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                      PNG, JPG, SVG recommended · Max 2MB<br />
                      If no logo uploaded, favicon will be auto-fetched.
                    </p>
                  </div>
                </div>
              </div>

              <div className="form-group"><label>Platform Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
              <div className="form-group"><label>Download URL *</label><input value={form.downloadUrl} onChange={e => setForm({ ...form, downloadUrl: e.target.value })} required placeholder="https://..." /></div>
              <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Short description shown on the card..." /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label>Label</label>
                  <select value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}>
                    <option value="">None</option>
                    <option value="HOT">HOT</option>
                    <option value="NEW">NEW</option>
                    <option value="TRENDING">TRENDING</option>
                    <option value="TOP">TOP</option>
                  </select>
                </div>
                <div className="form-group"><label>Display Order</label><input type="number" value={form.order} onChange={e => setForm({ ...form, order: +e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={form.isHottest} onChange={e => setForm({ ...form, isHottest: e.target.checked })} style={{ width: 'auto' }} /> 🔥 Mark as Hottest
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} style={{ width: 'auto' }} /> Active
                </label>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editing ? 'Update Platform' : 'Create Platform'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
